import { useEffect, useRef } from "react";
import { getCard, getOrganization } from "../content/lookups.js";
import { useGame } from "../app/GameProvider.js";
import { cardKindLabel } from "./cardFrame.js";

type CardSummary = {
  cardId: string;
  name: string;
  text: string;
  kind: string;
  count: number;
};

export function DetailSheet() {
  const { state, dispatch } = useGame();
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.overlay !== "deck" && state.overlay !== "log") return;
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch({ type: "CLOSE_OVERLAY" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.overlay, dispatch]);

  if (state.overlay !== "deck" && state.overlay !== "log") return null;
  const run = state.run;
  if (!run) return null;

  const zones = [run.deck, run.hand, run.discard, run.inPlay, run.aside, run.caseExcluded ?? []];
  const deckTotal = zones.reduce((sum, cards) => sum + cards.length, 0);
  const internCount = zones.flat().filter((card) => card.cardId === "intern").length;
  const orgLines = run.organizations.map((id) => ({
    id,
    name: getOrganization(id)?.name ?? id,
    text: getOrganization(id)?.text ?? "効果不明",
  }));
  const cardSummaries = zones.flat().reduce<Record<string, CardSummary>>((summaries, card) => {
    const definition = getCard(card.cardId);
    const existing = summaries[card.cardId];
    if (existing) {
      existing.count += 1;
    } else {
      summaries[card.cardId] = {
        cardId: card.cardId,
        name: definition?.name ?? card.cardId,
        text: definition?.text ?? "効果不明",
        kind: definition ? cardKindLabel(definition) : "不明",
        count: 1,
      };
    }
    return summaries;
  }, {});

  return (
    <div className="overlay" role="presentation" onClick={() => dispatch({ type: "CLOSE_OVERLAY" })}>
      <div
        ref={sheetRef}
        className="sheet detail-sheet"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-sheet-body">
          <h2>詳細</h2>
          {run.activeCase?.actionDrawLimit != null && <section aria-label="情報共有の制限">
            <h3>情報共有の制限</h3>
            <p>このラウンドは、施策1回につき最大{run.activeCase.actionDrawLimit}枚までドローできます。通常の手札補充は対象外です。</p>
          </section>}
          <p>
            デッキ {deckTotal} / 山札 {run.deck.length} / 手札 {run.hand.length} / 捨て札{" "}
            {run.discard.length} / 使用済み {run.inPlay.length}
          </p>
          <h2 style={{ marginTop: 16 }}>カード一覧</h2>
          {(run.caseExcluded?.length ?? 0) > 0 && <section aria-label="プロジェクト中の一時除外">
            <h3>プロジェクト中の一時除外</h3>
            <p>元のカードはプロジェクト終了時に戻り、PJ限定のカードは消えます。</p>
            <ul>{run.caseExcluded!.map((card) => <li key={card.instanceId}>{getCard(card.cardId)?.name ?? card.cardId}</li>)}</ul>
          </section>}
          <ul className="detail-card-list">
            {Object.values(cardSummaries).sort((a, b) => a.name.localeCompare(b.name, "ja")).map((card) => (
              <li key={card.cardId}>
                <strong>{card.name} × {card.count}</strong>
                <small>{card.kind}</small>
                <span>{card.text}</span>
              </li>
            ))}
          </ul>
          <h2 style={{ marginTop: 16 }}>組織基盤</h2>
          <ul className="detail-org-list">{orgLines.length ? orgLines.map((org) => (
            <li key={org.id}>
              <strong>{org.name}</strong>
              <span>{org.text}</span>
              {org.id === "expansion-strategy" ? <small>新卒 {internCount} / 15</small> : null}
            </li>
          )) : <li>まだない</li>}</ul>
        </div>
        <div className="detail-sheet-actions">
          <button type="button" className="btn btn-danger" onClick={() => dispatch({ type: "OPEN_OVERLAY", overlay: "abandonConfirm" })}>
            ゲームをあきらめる
          </button>
          <button type="button" className="btn" onClick={() => dispatch({ type: "CLOSE_OVERLAY" })}>
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
